/**
 * CmsNavContext — fetches and caches the portal navigation from the Nav Builder API.
 *
 * Fetches header and footer menus in parallel at boot.
 * Exposes headerItems, footerItems, and a generic useCmsMenu(slug) hook.
 * Also exposes homepageSlug so App.tsx can render the right component at /.
 */

import { createContext, useContext, useState, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CmsNavItem {
  id: string;
  label: string;
  /** 'page' | 'collection' | 'external' */
  type: string;
  /** Resolved portal path, e.g. /p/about or /c/blog or https://example.com */
  path: string;
  groupLabel: string | null;
  sortOrder: number;
  isExternal: boolean;
}

export interface CmsNavGroup {
  /** null = ungrouped items */
  label: string | null;
  items: CmsNavItem[];
}

interface CmsNavState {
  /** All nav items (legacy, default menu) */
  navItems: CmsNavItem[];
  navGroups: CmsNavGroup[];
  /** Items from the "header" menu */
  headerItems: CmsNavItem[];
  /** Items from the "footer" menu */
  footerItems: CmsNavItem[];
  /** Lookup by menu slug */
  menuItemsBySlug: Record<string, CmsNavItem[]>;
  homepageSlug: string | null;
  isLoading: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const CmsNavContext = createContext<CmsNavState>({
  navItems: [],
  navGroups: [],
  headerItems: [],
  footerItems: [],
  menuItemsBySlug: {},
  homepageSlug: null,
  isLoading: true,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTenantSlug(): string {
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug) return envSlug;
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 2 && parts[0] !== 'localhost') return parts[0];
  return 'tenant1';
}

async function fetchNavByMenu(
  spaceId: string | null,
  menuSlug?: string,
): Promise<{ homepageSlug: string | null; items: CmsNavItem[] }> {
  try {
    const headers: Record<string, string> = { 'x-tenant-slug': getTenantSlug() };
    if (spaceId) headers['x-space-id'] = spaceId;
    const url = menuSlug
      ? `/customer/portal/nav?menu=${encodeURIComponent(menuSlug)}`
      : '/customer/portal/nav';
    const res = await fetch(url, { headers });
    if (!res.ok) return { homepageSlug: null, items: [] };
    return (await res.json()) as { homepageSlug: string | null; items: CmsNavItem[] };
  } catch {
    return { homepageSlug: null, items: [] };
  }
}

/** Groups a flat list of nav items by groupLabel, preserving order within groups. */
function groupNavItems(items: CmsNavItem[]): CmsNavGroup[] {
  const groups: CmsNavGroup[] = [];
  const seen = new Map<string | null, CmsNavGroup>();

  for (const item of items) {
    const key = item.groupLabel ?? null;
    if (!seen.has(key)) {
      const group: CmsNavGroup = { label: key, items: [] };
      seen.set(key, group);
      groups.push(group);
    }
    seen.get(key)!.items.push(item);
  }

  return groups;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function CmsNavProvider({
  children,
  defaultSpaceId,
}: {
  children: React.ReactNode;
  defaultSpaceId: string | null;
}) {
  const [state, setState] = useState<CmsNavState>({
    navItems: [],
    navGroups: [],
    headerItems: [],
    footerItems: [],
    menuItemsBySlug: {},
    homepageSlug: null,
    isLoading: true,
  });

  useEffect(() => {
    let mounted = true;

    // Fetch default (all), header, and footer menus in parallel
    Promise.all([
      fetchNavByMenu(defaultSpaceId),
      fetchNavByMenu(defaultSpaceId, 'header'),
      fetchNavByMenu(defaultSpaceId, 'footer'),
    ]).then(([defaultNav, headerNav, footerNav]) => {
      if (!mounted) return;
      setState({
        navItems: defaultNav.items,
        navGroups: groupNavItems(defaultNav.items),
        headerItems: headerNav.items,
        footerItems: footerNav.items,
        menuItemsBySlug: {
          header: headerNav.items,
          footer: footerNav.items,
        },
        homepageSlug: defaultNav.homepageSlug,
        isLoading: false,
      });
    });

    return () => {
      mounted = false;
    };
  }, [defaultSpaceId]);

  return <CmsNavContext.Provider value={state}>{children}</CmsNavContext.Provider>;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useCmsNav(): CmsNavItem[] {
  return useContext(CmsNavContext).navItems;
}

export function useCmsNavGroups(): CmsNavGroup[] {
  return useContext(CmsNavContext).navGroups;
}

export function useCmsHeaderNav(): CmsNavItem[] {
  return useContext(CmsNavContext).headerItems;
}

export function useCmsFooterNav(): CmsNavItem[] {
  return useContext(CmsNavContext).footerItems;
}

/** Returns items for an arbitrary menu slug. Falls back to all items if not found. */
export function useCmsMenu(slug: string): CmsNavItem[] {
  const ctx = useContext(CmsNavContext);
  return ctx.menuItemsBySlug[slug] ?? ctx.navItems;
}

export function useCmsHomepageSlug(): string | null {
  return useContext(CmsNavContext).homepageSlug;
}

export function useCmsNavLoading(): boolean {
  return useContext(CmsNavContext).isLoading;
}
